// The backstop's pattern subset (D-100, T0 ruling 3 Nov 2026). A pattern from a pack, an atlas or a
// tenant is accepted only when it is written in a restrictive subset of JavaScript regular
// expressions whose worst case is bounded by its own size — decided from the source alone, with no
// clock and no input run. The same source gets the same answer on every machine.
//
// Accepted:
//   - literals, character classes (and \d \w \s, their negations, and .), the anchors ^ $ \b \B;
//   - quantifiers with an upper bound of at most PACK_MAX_REPEAT: ?, {n}, {m,n} — never +, * or {n,};
//   - groups that do not nest, whose body is one alternative or several, each a literal string or a
//     single class, with no quantifier inside; a group may itself take a bounded quantifier;
//   - edge guards (T0, 3 Nov 2026): one negative lookbehind (?<![…]) as the very first item and/or one
//     negative lookahead (?![…]) as the very last, each holding one bracketed class whose only
//     escapes are \d \D \w \W \s \S. Each counts in the step bound.
// Refused: a pattern whose shortest match is empty (\b, ^, a{0,3}; edge guards do not count as
// length), backreferences, every other lookaround (positive, in the middle, two at one edge, with a
// group, alternation, quantifier or other escape inside), nested groups, a quantifier inside a quantified group, an
// empty alternative, more than PACK_MAX_ALTERNATIVES alternatives in all, a source longer than
// PACK_MAX_SOURCE, and anything the parser does not read.
//
// The bound. A backtracking matcher tries, from one start position, at most one path per way of
// choosing every item's count or alternative: P = the product over the items of their choices (a
// quantifier {m,n} has n − m + 1; a group of b alternatives repeated {m,n} has the sum of b^k for k
// from m to n). Each path tests at most I + M characters and assertions, I the number of items and M
// the longest match. So steps per start ≤ P × (I + M), and the subset refuses a pattern whose bound
// exceeds PACK_STEP_BUDGET. Per scan window that is at most (SCAN_WINDOW + SCAN_OVERLAP) × budget
// tests, however adversarial the input. The test suite checks the bound against a reference
// backtracking matcher that counts its steps.
//
// The built-in patterns are not held to the subset (they use lookbehind and backreferences); they are
// vetted in CI with the same reference matcher and trusted at run time by id and source.
const emptySet = () => ({ bits: new Uint32Array(8), other: false });
const setOf = (...codes) => {
    const s = emptySet();
    for (const c of codes)
        add(s, c);
    return s;
};
function add(s, c) {
    if (c > 255)
        s.other = true;
    else
        s.bits[c >> 5] = (s.bits[c >> 5] ?? 0) | (1 << (c & 31));
}
function addRange(s, from, to) {
    for (let c = from; c <= Math.min(to, 255); c += 1)
        add(s, c);
    if (to > 255)
        s.other = true;
}
const has = (s, c) => ((s.bits[c >> 5] ?? 0) & (1 << (c & 31))) !== 0;
function union(a, b) {
    const s = emptySet();
    for (let i = 0; i < 8; i += 1)
        s.bits[i] = (a.bits[i] ?? 0) | (b.bits[i] ?? 0);
    s.other = a.other || b.other;
    return s;
}
function complement(a) {
    const s = emptySet();
    for (let i = 0; i < 8; i += 1)
        s.bits[i] = ~(a.bits[i] ?? 0) >>> 0;
    s.other = true;
    s.of = a;
    return s;
}
/** The case-insensitive closure of a set (Latin-1 pairs; anything that folds above 255 is "other"). */
function fold(a) {
    const s = union(a, emptySet());
    for (let c = 0; c <= 255; c += 1) {
        if (!has(a, c))
            continue;
        const ch = String.fromCharCode(c);
        for (const v of [ch.toLowerCase(), ch.toUpperCase()]) {
            const code = v.length === 1 ? v.charCodeAt(0) : 256;
            if (code !== c)
                add(s, code);
        }
    }
    return s;
}
const DIGIT = () => {
    const s = emptySet();
    addRange(s, 48, 57);
    return s;
};
const WORD = () => {
    const s = DIGIT();
    addRange(s, 65, 90);
    addRange(s, 97, 122);
    add(s, 95);
    return s;
};
const SPACE = () => {
    const s = setOf(9, 10, 11, 12, 13, 32, 0xa0);
    s.other = true; // U+1680, U+2000–U+200A, U+2028/9, U+202F, U+205F, U+3000, U+FEFF
    return s;
};
const ANY = () => complement(setOf(10, 13)); // also not U+2028/9, which "other" covers
class Unsupported extends Error {
}
/** Parse a pattern source as `new RegExp(src, "i")` reads it (no u flag: Annex B syntax). */
function parse(src) {
    let i = 0;
    let captures = 0;
    const groups = new Map();
    const fail = () => {
        throw new Unsupported();
    };
    const peek = (k = 0) => src[i + k] ?? "";
    const hex = (n) => {
        const h = src.slice(i, i + n);
        if (h.length !== n || !/^[0-9a-f]+$/i.test(h))
            return -1;
        i += n;
        return parseInt(h, 16);
    };
    /** An escape after the backslash; inClass changes \b and forbids backreferences. */
    const escape = (inClass) => {
        const c = peek();
        i += 1;
        switch (c) {
            case "d":
                return DIGIT();
            case "D":
                return complement(DIGIT());
            case "w":
                return WORD();
            case "W":
                return complement(WORD());
            case "s":
                return SPACE();
            case "S":
                return complement(SPACE());
            case "t":
                return setOf(9);
            case "n":
                return setOf(10);
            case "v":
                return setOf(11);
            case "f":
                return setOf(12);
            case "r":
                return setOf(13);
            case "0":
                if (/[0-9]/.test(peek()))
                    return fail();
                return setOf(0);
            case "x": {
                const v = hex(2);
                return v < 0 ? setOf(120) : setOf(v);
            }
            case "u": {
                const v = hex(4);
                return v < 0 ? setOf(117) : setOf(v);
            }
            case "c": {
                const l = peek();
                if (/[a-z]/i.test(l)) {
                    i += 1;
                    return setOf(l.charCodeAt(0) % 32);
                }
                return fail();
            }
            case "b":
                return inClass ? setOf(8) : { t: "assert", kind: "b" };
            case "B":
                return inClass ? fail() : { t: "assert", kind: "B" };
            case "k": {
                if (inClass || peek() !== "<")
                    return fail();
                const m = /^<([A-Za-z_$][\w$]*)>/.exec(src.slice(i));
                if (m === null)
                    return fail();
                i += m[0].length;
                return { t: "backref", ref: m[1] ?? "" };
            }
            case "p":
            case "P":
                return fail(); // a property escape means the author expects the u flag, which is not set
            case "":
                return fail();
            default:
                if (/[1-9]/.test(c)) {
                    if (inClass)
                        return fail();
                    let n = c;
                    while (/[0-9]/.test(peek())) {
                        n += peek();
                        i += 1;
                    }
                    return { t: "backref", ref: Number(n) };
                }
                return setOf(c.charCodeAt(0)); // identity escape: \. \- \/ …
        }
    };
    const classAtom = () => {
        if (peek() === "\\") {
            i += 1;
            const e = escape(true);
            if (!("bits" in e))
                return fail();
            return e;
        }
        const c = src.charCodeAt(i);
        i += 1;
        return setOf(c);
    };
    const charClass = () => {
        i += 1; // [
        const negate = peek() === "^";
        if (negate)
            i += 1;
        let s = emptySet();
        while (peek() !== "]") {
            if (i >= src.length)
                fail();
            const a = classAtom();
            if (peek() === "-" && peek(1) !== "]" && peek(1) !== "") {
                const save = i;
                i += 1;
                const b = classAtom();
                const single = (x) => {
                    let found = -1;
                    for (let c = 0; c <= 255; c += 1)
                        if (has(x, c)) {
                            if (found >= 0)
                                return -1;
                            found = c;
                        }
                    return x.other ? -1 : found;
                };
                const lo = single(a);
                const hi = single(b);
                if (lo >= 0 && hi >= 0) {
                    if (lo > hi)
                        fail();
                    const r = emptySet();
                    addRange(r, lo, hi);
                    s = union(s, r);
                    continue;
                }
                // a class escape at either end of a range: the dash is literal (Annex B), or the source
                // names a code unit above 255 that this set cannot spell exactly — either way, widen
                i = save;
                s = union(s, a);
                continue;
            }
            s = union(s, a);
        }
        i += 1; // ]
        return negate ? complement(s) : s;
    };
    /** A quantifier at i, or null. */
    const quantifier = () => {
        const c = peek();
        let q = null;
        if (c === "*")
            q = { min: 0, max: Infinity };
        else if (c === "+")
            q = { min: 1, max: Infinity };
        else if (c === "?")
            q = { min: 0, max: 1 };
        else if (c === "{") {
            const m = /^\{(\d+)(,(\d*))?\}/.exec(src.slice(i));
            if (m === null)
                return null; // a literal brace (Annex B)
            const min = Number(m[1]);
            const max = m[2] === undefined ? min : m[3] === "" ? Infinity : Number(m[3]);
            if (max < min)
                fail();
            i += m[0].length - 1;
            q = { min, max };
        }
        if (q === null)
            return null;
        i += 1;
        if (peek() === "?")
            i += 1; // lazy: the same paths in another order
        return q;
    };
    const term = () => {
        const c = peek();
        let atom;
        if (c === "|" || c === ")" || c === "")
            return null;
        if (c === "(") {
            i += 1;
            let kind = "group";
            let behind = false;
            let negative = false;
            let index = null;
            let name = null;
            if (peek() === "?") {
                const rest = src.slice(i + 1);
                if (rest.startsWith(":"))
                    i += 2;
                else if (rest.startsWith("=") || rest.startsWith("!")) {
                    kind = "look";
                    negative = rest.startsWith("!");
                    i += 2;
                }
                else if (rest.startsWith("<=") || rest.startsWith("<!")) {
                    kind = "look";
                    behind = true;
                    negative = rest.startsWith("<!");
                    i += 3;
                }
                else {
                    const m = /^<([A-Za-z_$][\w$]*)>/.exec(rest);
                    if (m === null)
                        return fail(); // flag groups and anything newer
                    i += 1 + m[0].length;
                    name = m[1] ?? "";
                    captures += 1;
                    index = captures;
                }
            }
            else {
                captures += 1;
                index = captures;
            }
            const body = alternation();
            if (peek() !== ")")
                fail();
            i += 1;
            if (kind === "look")
                atom = { t: "look", body, behind, negative };
            else {
                atom = { t: "group", body, index };
                if (index !== null)
                    groups.set(index, body);
                if (name !== null)
                    groups.set(name, body);
            }
            // a lookahead may take a quantifier in Annex B; a lookbehind may not
            if (kind === "look" && behind)
                return atom;
        }
        else if (c === "[")
            atom = { t: "set", set: charClass() };
        else if (c === "\\") {
            i += 1;
            const e = escape(false);
            atom = "bits" in e ? { t: "set", set: e } : e;
        }
        else if (c === "^" || c === "$") {
            i += 1;
            return { t: "assert", kind: c };
        }
        else if (c === ".") {
            i += 1;
            atom = { t: "set", set: ANY() };
        }
        else if (c === "*" || c === "+" || c === "?")
            return fail();
        else {
            i += 1;
            atom = { t: "set", set: setOf(c.charCodeAt(0)) };
        }
        if (atom.t === "assert")
            return atom; // \b \B take no quantifier
        const q = quantifier();
        return q === null ? atom : { t: "rep", body: atom, min: q.min, max: q.max };
    };
    const sequence = () => {
        const items = [];
        for (let t = term(); t !== null; t = term())
            items.push(t);
        return items.length === 1 ? items[0] : { t: "seq", items };
    };
    const alternation = () => {
        const alts = [sequence()];
        while (peek() === "|") {
            i += 1;
            alts.push(sequence());
        }
        return alts.length === 1 ? alts[0] : { t: "alt", alts };
    };
    const root = alternation();
    if (i !== src.length)
        fail();
    for (const ref of backrefs(root))
        if (!groups.has(ref))
            fail();
    return { root, groups };
}
function backrefs(n, out = []) {
    switch (n.t) {
        case "backref":
            out.push(n.ref);
            break;
        case "seq":
            for (const x of n.items)
                backrefs(x, out);
            break;
        case "alt":
            for (const x of n.alts)
                backrefs(x, out);
            break;
        case "rep":
        case "group":
        case "look":
            backrefs(n.body, out);
            break;
        default:
    }
    return out;
}
/** The longest source the subset accepts (an atlas regex is at most 200 characters as well). */
export const PACK_MAX_SOURCE = 200;
/** The largest upper bound a quantifier may have. */
export const PACK_MAX_REPEAT = 64;
/** Alternatives in all, across every group and the top level. */
export const PACK_MAX_ALTERNATIVES = 16;
/** Steps per start position, at most: the bound P × (I + M) above. */
export const PACK_STEP_BUDGET = 2048;
// An edge guard as written: one bracketed class whose only escapes are \d \D \w \W \s \S.
const GUARD_CLASS = String.raw `\[(?:[^\\\]]|\\[dDwWsS])*\]`;
const LEAD = new RegExp(String.raw `^\(\?<!` + GUARD_CLASS + String.raw `\)`);
const TRAIL = new RegExp(String.raw `\(\?!` + GUARD_CLASS + String.raw `\)$`);
/** A lookaround at the pattern's edge whose body is one plain bracketed class, as LEAD/TRAIL read it. */
function edgeGuard(n, writtenAsGuard) {
    return n.body.t === "set" && writtenAsGuard;
}
class Outside extends Error {
    rule;
    constructor(rule) {
        super(rule);
        this.rule = rule;
    }
}
/** A literal string or a single class: what an alternative inside a group may be. Its length. */
function simpleBranch(n) {
    if (n.t === "set")
        return 1;
    if (n.t === "seq") {
        if (n.items.length === 0)
            throw new Outside("empty-alternative");
        for (const x of n.items)
            if (x.t !== "set")
                simpleBranch(x);
        return n.items.length;
    }
    if (n.t === "group" || n.t === "look")
        throw new Outside(n.t === "look" ? "lookaround" : "nested-group");
    if (n.t === "rep")
        throw new Outside("nested-quantifier");
    if (n.t === "backref")
        throw new Outside("backreference");
    throw new Outside("alternation");
}
/** The alternatives of a group body or of the whole pattern: each simple, none empty. */
function alternatives(n) {
    const branches = n.t === "alt" ? n.alts : [n];
    let longest = 0;
    let shortest = Infinity;
    for (const b of branches) {
        const len = simpleBranch(b);
        longest = Math.max(longest, len);
        shortest = Math.min(shortest, len);
    }
    return { count: branches.length, longest, shortest };
}
const capped = (x) => Math.min(x, Number.MAX_SAFE_INTEGER);
/**
 * Is the pattern inside the pack subset, and what is its step bound per start position? See the top
 * of this file. Deterministic: reads the source only.
 */
export function checkPackPattern(src) {
    if (src.length > PACK_MAX_SOURCE)
        return { ok: false, rule: "too-long" };
    let root;
    try {
        root = parse(src).root;
    }
    catch {
        return { ok: false, rule: "invalid" };
    }
    try {
        let paths = 1;
        let tests = 0; // I + M
        let alts = 0;
        let least = 0; // the shortest match, guards not counted (T0: zero-length patterns are refused)
        let top = root.t === "alt" ? null : root.t === "seq" ? root.items : [root];
        if (top !== null) {
            // the edge guards (T0, 3 Nov 2026): one (?<![…]) first and/or one (?![…]) last
            const first = top[0];
            if (first?.t === "look" &&
                first.behind &&
                first.negative &&
                edgeGuard(first, LEAD.test(src))) {
                top = top.slice(1);
                tests += 2;
            }
            const last = top.at(-1);
            if (last?.t === "look" && !last.behind && last.negative && edgeGuard(last, TRAIL.test(src))) {
                top = top.slice(0, -1);
                tests += 2;
            }
        }
        if (top === null) {
            // a top-level alternation: of literals or classes only
            const a = alternatives(root);
            alts += a.count;
            paths = a.count;
            tests = a.longest + 1;
            least = a.shortest;
        }
        else {
            if (top.length === 0)
                throw new Outside("empty-alternative");
            for (const item of top) {
                let atom = item;
                let min = 1;
                let max = 1;
                if (item.t === "rep") {
                    atom = item.body;
                    min = item.min;
                    max = item.max;
                    if (max === Infinity)
                        throw new Outside("unbounded");
                    if (max > PACK_MAX_REPEAT)
                        throw new Outside("wide");
                }
                switch (atom.t) {
                    case "set":
                        paths = capped(paths * (max - min + 1));
                        tests += max + 1;
                        least += min;
                        break;
                    case "assert":
                        tests += 1;
                        break;
                    case "group": {
                        const a = alternatives(atom.body);
                        alts += a.count;
                        let choices = 0;
                        for (let k = min; k <= max; k += 1)
                            choices = capped(choices + a.count ** k);
                        paths = capped(paths * choices);
                        tests += max * a.longest + 1;
                        least += min * a.shortest;
                        break;
                    }
                    case "look":
                        throw new Outside("lookaround");
                    case "backref":
                        throw new Outside("backreference");
                    default:
                        throw new Outside("invalid");
                }
            }
        }
        if (alts > PACK_MAX_ALTERNATIVES)
            throw new Outside("alternation");
        if (least === 0)
            throw new Outside("zero-length");
        const steps = capped(paths * tests);
        if (steps > PACK_STEP_BUDGET)
            throw new Outside("steps");
        return { ok: true, steps };
    }
    catch (e) {
        if (e instanceof Outside)
            return { ok: false, rule: e.rule };
        throw e;
    }
}
/** The parsed pattern, for the test suite's reference matcher. Throws on syntax the parser refuses. */
export function parsePattern(src) {
    return { root: parse(src).root };
}
/** Does the set hold this code unit, compared as the i flag compares? (Above 255: the "other" flag.) */
const folded = new WeakMap();
export function setHas(set, code, ignoreCase = true) {
    let s = set.set;
    if (ignoreCase) {
        const known = folded.get(s);
        if (known === undefined) {
            const of = s.of;
            folded.set(s, (s = of === undefined ? fold(s) : complement(fold(of))));
        }
        else
            s = known;
    }
    return code > 255 ? s.other : has(s, code);
}
//# sourceMappingURL=analyse.js.map